import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import {
  KlausOrgOption,
  useKlausLinkableOrgsQuery,
  useKlausSync,
  useLinkCompanyToKlausMutation,
  useUnlinkCompanyFromKlausMutation,
} from '@dieterApi/company/useKlausLinkMutations';
import { UserRole } from '@dieterApi/user/useUserQuery';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';

/**
 * Klaus-Verknüpfung der aktiven Firma (Klaus ↔ Dieter Sync, Addendum v1.2) —
 * das Gegenstück zur Dieter-Seite in Klaus. Alle Mitglieder sehen, ob und mit
 * welcher Klaus-Organisation die Firma verknüpft ist; Admins verknüpfen und
 * heben auf. Eine Kanzlei mit Mandanten pflegt das pro Mandant im
 * Mandanten-Tab; die Kanzlei selbst wird nicht verknüpft.
 */
export function KlausLink() {
  const { user, activeCompany } = useUserWithCompany();
  const { enqueueSnackbar } = useSnackbar();
  const [loadOrgs, { data, loading }] = useKlausLinkableOrgsQuery();
  const [link, { loading: linking }] = useLinkCompanyToKlausMutation();
  const [unlink, { loading: unlinking }] = useUnlinkCompanyFromKlausMutation();
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const klausSync = useKlausSync();

  // The active company carries the features; the link sits on the user's
  // memberships (Company.klausLink, members only).
  const company = activeCompany;
  const isAdmin = user?.role === UserRole.Admin;
  const isKanzlei = Boolean(
    company?.isPrincipal && company.features?.some((f) => f === 'MultiTenancy' || f === 'DpoManaged')
  );
  const klausLink = user?.companies?.find((c) => c.id === company?.id)?.klausLink ?? null;

  useEffect(() => {
    if (isAdmin && company && !klausLink && !isKanzlei) loadOrgs();
  }, [isAdmin, company?.id, klausLink, isKanzlei, loadOrgs]);

  const orgs: KlausOrgOption[] = data?.klausLinkableOrgs ?? [];

  const handleLink = (org: KlausOrgOption) => {
    if (!company) return;
    link({ variables: { companyId: company.id, klausOrgId: org.id } })
      .then(() =>
        enqueueSnackbar(`Mit „${org.name}“ in Klaus verknüpft. Klaus übernimmt die Mitglieder aus Dieter.`, {
          variant: 'success',
        })
      )
      .catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
  };

  const handleUnlink = () => {
    if (!company) return;
    unlink({ variables: { companyId: company.id } })
      .then(() => {
        setConfirmUnlink(false);
        enqueueSnackbar('Verknüpfung aufgehoben. Eure Inhalte in Klaus und Dieter bleiben erhalten.', {
          variant: 'success',
        });
      })
      .catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
  };

  // Without the coupling (Prod before Klaus) there is nothing here: back to
  // the dashboard, once Dieter has answered.
  if (!klausSync.enabled) {
    return klausSync.loading ? null : <Redirect to="/dashboard" />;
  }

  return (
    <div className="2xl:container flex flex-col p-6 gap-4 max-w-3xl">
      <h1>Klaus-Verknüpfung</h1>
      <p>
        Nutzt ihr auch Klaus kann KI für eure KI-Compliance? Verknüpft eure Firma mit eurer Organisation in Klaus. Dann
        arbeitet ihr in beiden Apps mit denselben Personen: Wer hier zur Firma gehört, kann auch in Klaus mitarbeiten.
      </p>
      <div className="min-h-[40px] flex items-center justify-between gap-4">
        <Button
          variant="contained"
          href="/zu-klaus?src=klaus-link"
          target="_blank"
          rel="noopener"
          data-testid="klaus-open"
        >
          Klaus öffnen
        </Button>
        <Link to="/dashboard">Zurück</Link>
      </div>

      {isKanzlei ? (
        <section className="rounded-lg bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold">Verknüpfungen pro Mandant</h2>
          <p className="mt-2">
            Eure Kanzlei selbst wird nicht mit Klaus verknüpft, nur ihre Mandanten. Das geht im Dashboard im Tab
            „Mandanten“ in der Spalte „Klaus“.
          </p>
        </section>
      ) : klausLink ? (
        <section className="rounded-lg bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold">Verknüpft mit Klaus</h2>
            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">Aktiv</span>
          </div>
          <p className="mt-2">
            „{company?.name}“ ist mit {klausLink.klausOrgName ? `„${klausLink.klausOrgName}“` : 'einer Organisation'} in
            Klaus verknüpft. Wer hier zur Firma gehört, kann dort mitarbeiten. Personen fügt ihr hier in Dieter hinzu
            oder entfernt sie.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Verknüpft von {klausLink.createdByEmail} am {new Date(klausLink.createdAt).toLocaleDateString('de-DE')}
          </p>
          {isAdmin && (
            <div className="mt-4">
              <Button variant="outlined" onClick={() => setConfirmUnlink(true)}>
                Verknüpfung aufheben
              </Button>
            </div>
          )}
        </section>
      ) : (
        <section className="rounded-lg bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold">Nicht mit Klaus verknüpft</h2>
          {!isAdmin ? (
            <p className="mt-2">Verknüpfen können nur Admins eurer Firma.</p>
          ) : loading ? (
            <p className="mt-2">Klaus wird gefragt …</p>
          ) : orgs.length > 0 ? (
            <>
              <p className="mt-2">Wähle die Organisation aus Klaus, die zu „{company?.name}“ gehört.</p>
              <ul className="mt-3 flex flex-col gap-2">
                {orgs.map((org) => (
                  <li key={org.id} className="flex items-center justify-between gap-4 rounded-md border px-4 py-3">
                    <span className="font-medium">{org.name || 'Unbenannte Organisation'}</span>
                    <Button variant="contained" disabled={linking} onClick={() => handleLink(org)}>
                      Verknüpfen
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mt-2">
              Unter deiner E-Mail-Adresse gibt es in Klaus keine Organisation, die du verknüpfen kannst. Melde dich bei
              Klaus kann KI mit denselben Zugangsdaten wie hier an, danach kannst du sie hier verknüpfen.
            </p>
          )}
        </section>
      )}

      <Dialog open={confirmUnlink} onClose={() => setConfirmUnlink(false)}>
        <DialogTitle>Verknüpfung aufheben?</DialogTitle>
        <DialogContent>
          Klaus behält die Personen, die bisher Zugriff hatten, und eure Inhalte bleiben in beiden Apps erhalten.
          Änderungen an den Mitgliedern hier kommen danach nicht mehr in Klaus an.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnlink(false)}>Abbrechen</Button>
          <Button variant="contained" color="error" disabled={unlinking} onClick={handleUnlink}>
            Aufheben
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
